/**
 * 2D map camera: centre (mu) + zoom (CSS px per mu), with drag inertia and smooth
 * cursor-anchored zoom. Pure math, no DOM (tested in test/camera.test.ts).
 */
export interface Viewport {
  /** CSS pixels */
  w: number;
  h: number;
}

const MAX_ZOOM = 48;
/** fraction of velocity kept after 1 s of free flight */
const FRICTION_PER_S = 0.004;
/** smooth zoom: fraction of remaining log-distance covered per second */
const ZOOM_EASE = 14;
/** glide: fraction of remaining distance covered per second */
const FLY_EASE = 7;

export class Camera {
  cx: number;
  cy: number;
  zoom: number;
  /** pan velocity, mu/s */
  vx = 0;
  vy = 0;
  private targetZoom: number;
  private anchor: { sx: number; sy: number } | null = null;
  /** smooth glide target (centre), cancelled by manual panning */
  private fly: { x: number; y: number } | null = null;
  vp: Viewport = { w: 1, h: 1 };

  constructor(
    readonly worldW: number,
    readonly worldH: number,
  ) {
    this.cx = worldW / 2;
    this.cy = worldH / 2;
    this.zoom = 6;
    this.targetZoom = 6;
  }

  /** Smallest zoom: the whole world fits on screen. */
  get minZoom(): number {
    return Math.min(this.vp.w / this.worldW, this.vp.h / this.worldH);
  }

  get maxZoom(): number {
    return MAX_ZOOM;
  }

  resize(w: number, h: number): void {
    this.vp = { w: Math.max(1, w), h: Math.max(1, h) };
    this.zoom = this.clampZoom(this.zoom);
    this.targetZoom = this.clampZoom(this.targetZoom);
    this.clamp();
  }

  clampZoom(z: number): number {
    return Math.min(MAX_ZOOM, Math.max(this.minZoom, z));
  }

  worldToScreen(x: number, y: number): [number, number] {
    return [(x - this.cx) * this.zoom + this.vp.w / 2, (y - this.cy) * this.zoom + this.vp.h / 2];
  }

  screenToWorld(sx: number, sy: number): [number, number] {
    return [(sx - this.vp.w / 2) / this.zoom + this.cx, (sy - this.vp.h / 2) / this.zoom + this.cy];
  }

  /** Jump (no animation) to centre (x, y) at zoom z. */
  lookAt(x: number, y: number, z = this.zoom): void {
    this.zoom = this.targetZoom = this.clampZoom(z);
    this.cx = x;
    this.cy = y;
    this.vx = this.vy = 0;
    this.anchor = null;
    this.clamp();
  }

  /** Glide the view centre to (x, y), easing the zoom to `z` on the way if given. */
  flyTo(x: number, y: number, z?: number): void {
    this.fly = { x, y };
    this.vx = this.vy = 0;
    if (z !== undefined) {
      this.targetZoom = this.clampZoom(z);
      this.anchor = null;
    }
  }

  /** Drag by a screen-space delta (CSS px). */
  panBy(dsx: number, dsy: number): void {
    this.fly = null;
    this.cx -= dsx / this.zoom;
    this.cy -= dsy / this.zoom;
    this.clamp();
  }

  /** Release a drag with a screen-space velocity (CSS px/s) for inertia. */
  fling(vsx: number, vsy: number): void {
    this.vx = -vsx / this.zoom;
    this.vy = -vsy / this.zoom;
  }

  stop(): void {
    this.vx = this.vy = 0;
  }

  /** Zoom by factor around a screen point; animated unless `instant`. */
  zoomAt(factor: number, sx: number, sy: number, instant = false): void {
    this.targetZoom = this.clampZoom(this.targetZoom * factor);
    this.anchor = { sx, sy };
    if (instant) this.applyZoom(this.targetZoom);
  }

  /** Advance inertia, glides and smooth zoom. */
  update(dt: number): void {
    if (this.fly) {
      const k = Math.min(1, FLY_EASE * dt);
      this.cx += (this.fly.x - this.cx) * k;
      this.cy += (this.fly.y - this.cy) * k;
      if (Math.hypot(this.fly.x - this.cx, this.fly.y - this.cy) * this.zoom < 0.5) this.fly = null;
      this.clamp();
    }
    if (this.vx !== 0 || this.vy !== 0) {
      this.cx += this.vx * dt;
      this.cy += this.vy * dt;
      const k = Math.pow(FRICTION_PER_S, dt);
      this.vx *= k;
      this.vy *= k;
      if (Math.hypot(this.vx, this.vy) * this.zoom < 4) this.vx = this.vy = 0;
      this.clamp();
    }
    if (this.zoom !== this.targetZoom) {
      const lz = Math.log(this.zoom);
      const lt = Math.log(this.targetZoom);
      const next = Math.abs(lt - lz) < 1e-3 ? this.targetZoom : Math.exp(lz + (lt - lz) * Math.min(1, ZOOM_EASE * dt));
      this.applyZoom(next);
    }
  }

  private applyZoom(z: number): void {
    const a = this.anchor ?? { sx: this.vp.w / 2, sy: this.vp.h / 2 };
    const [wx, wy] = this.screenToWorld(a.sx, a.sy);
    this.zoom = z;
    // keep the anchored world point under the cursor
    this.cx = wx - (a.sx - this.vp.w / 2) / this.zoom;
    this.cy = wy - (a.sy - this.vp.h / 2) / this.zoom;
    if (z === this.targetZoom) this.anchor = null;
    this.clamp();
  }

  /** Keep the view over the map; centre the map on an axis where it is smaller than the view. */
  private clamp(): void {
    const hw = this.vp.w / 2 / this.zoom;
    const hh = this.vp.h / 2 / this.zoom;
    if (hw * 2 >= this.worldW) this.cx = this.worldW / 2;
    else this.cx = Math.min(this.worldW - hw, Math.max(hw, this.cx));
    if (hh * 2 >= this.worldH) this.cy = this.worldH / 2;
    else this.cy = Math.min(this.worldH - hh, Math.max(hh, this.cy));
  }

  /** Visible world rect [x0, y0, x1, y1] with a margin in CSS px. */
  visibleRect(marginPx = 0): [number, number, number, number] {
    const [x0, y0] = this.screenToWorld(-marginPx, -marginPx);
    const [x1, y1] = this.screenToWorld(this.vp.w + marginPx, this.vp.h + marginPx);
    return [x0, y0, x1, y1];
  }
}
